// File: frontend/src/components/Settings/PipelineSettings.jsx
// Pipeline stages configuration component

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ChartBarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { getPipelineStages, createPipelineStage, updatePipelineStage, deletePipelineStage } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';

const PipelineSettings = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    stage_order: '',
    win_probability: '',
    is_closed: false,
    is_won: false,
    color: '#6366f1',
    description: ''
  });

  const queryClient = useQueryClient();

  // Get pipeline stages
  const { data: stagesData, isLoading, error } = useQuery({
    queryKey: ['pipelineStages'],
    queryFn: getPipelineStages
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createPipelineStage,
    onSuccess: () => {
      queryClient.invalidateQueries(['pipelineStages']);
      setShowForm(false);
      resetForm();
      showNotification('success', 'Etapa creada exitosamente');
    },
    onError: (error) => {
      showNotification('error', error.response?.data?.message || 'Error al crear etapa');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updatePipelineStage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['pipelineStages']);
      setEditingStage(null);
      resetForm();
      showNotification('success', 'Etapa actualizada exitosamente');
    },
    onError: (error) => {
      showNotification('error', error.response?.data?.message || 'Error al actualizar etapa');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePipelineStage,
    onSuccess: () => {
      queryClient.invalidateQueries(['pipelineStages']);
      showNotification('success', 'Etapa eliminada exitosamente');
    },
    onError: (error) => {
      showNotification('error', error.response?.data?.message || 'Error al eliminar etapa');
    }
  });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      stage_order: '',
      win_probability: '',
      is_closed: false,
      is_won: false,
      color: '#6366f1',
      description: ''
    });
    setShowForm(false);
    setEditingStage(null);
  };

  const handleEdit = (stage) => {
    setEditingStage(stage);
    setFormData({
      name: stage.name,
      stage_order: stage.stage_order,
      win_probability: stage.win_probability,
      is_closed: stage.is_closed,
      is_won: stage.is_won,
      color: stage.color || '#6366f1',
      description: stage.description || ''
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      stage_order: parseInt(formData.stage_order),
      win_probability: parseFloat(formData.win_probability)
    };

    if (editingStage) {
      updateMutation.mutate({ id: editingStage.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (stage) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la etapa "${stage.name}"?`)) {
      deleteMutation.mutate(stage.id);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
        <p className="text-center text-gray-600 dark:text-gray-300 mt-4">Cargando etapas del pipeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Configuración de Pipeline no disponible
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Las etapas del pipeline se configuran desde la base de datos existente. Esta función estará disponible próximamente.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stages = stagesData?.data || [];

  return (
    <div className="p-6">
      {/* Notification */}
      {notification && (
        <div className={`mb-6 rounded-md p-4 ${
          notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex">
            {notification.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Etapas del Pipeline</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Configura las etapas del proceso comercial y sus probabilidades de cierre.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nueva Etapa
        </button>
      </div>

      {/* Pipeline Stages */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {stages.length === 0 ? (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">Sin etapas configuradas</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Comienza agregando la primera etapa de tu pipeline de ventas.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {stages.map((stage) => (
              <div key={stage.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: stage.color || '#6366f1' }}
                    />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stage.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{stage.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Orden: {stage.stage_order}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Probabilidad: {stage.win_probability}%
                    </div>
                    <div className="flex items-center space-x-2">
                      {stage.is_closed && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          stage.is_won 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {stage.is_won ? 'Ganada' : 'Perdida'}
                        </span>
                      )}
                      <button
                        onClick={() => handleEdit(stage)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(stage)}
                        className="text-red-600 hover:text-red-900"
                        disabled={deleteMutation.isPending}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              {editingStage ? 'Editar Etapa' : 'Nueva Etapa'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Orden</label>
                  <input
                    type="number"
                    value={formData.stage_order}
                    onChange={(e) => setFormData({ ...formData, stage_order: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Probabilidad %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.win_probability}
                    onChange={(e) => setFormData({ ...formData, win_probability: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Color</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  rows="2"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_closed}
                    onChange={(e) => setFormData({ ...formData, is_closed: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Etapa cerrada</span>
                </label>
                
                {formData.is_closed && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_won}
                      onChange={(e) => setFormData({ ...formData, is_won: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Ganada</span>
                  </label>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    editingStage ? 'Actualizar' : 'Crear'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineSettings;